import { IPrivateChatData } from "@home-health-notify/domain";
import { List } from "@mui/material";
import { useTranslation } from "hhn-commons";
import EmptyRecordView from "../../common/EmptyRecordView";
import GoToTop from "../../common/GoToTop";
import ScrollListContainer from "../../common/ScrollListContainer";
import { useScrollAnchor } from "../../hooks/useScrollAnchor";
import ProgressLoader from "../../loader/ProgressLoader";
import PrivateChatListItem from "./PrivateChatListItem";

type PrivateChatListProps = {
  chats: IPrivateChatData[];
  isChatLoading: boolean;
  isMoreChats: boolean;
  selectedChatId?: string;
  fetchChatList: () => Promise<void>;
};

export const PrivateChatList = ({
  chats,
  isChatLoading,
  selectedChatId,
  isMoreChats,
  fetchChatList,
}: PrivateChatListProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });

  const { scrollContainerRef, isShowGoToTopButton, goToTop } = useScrollAnchor({
    items: chats,
    getItemId: chat => chat.id,
    getItemVersion: chat => chat.updatedAt,
    selectedItemId: selectedChatId,
  });

  return (
    <ScrollListContainer
      ref={scrollContainerRef}
      isLoading={isChatLoading}
      dataTestId="privateChatList"
      onScrollCallback={fetchChatList}
      restTop={isChatLoading ? 200 : 154}
    >
      <>
        {isShowGoToTopButton && <GoToTop onClick={goToTop} />}
        {chats.length > 0 && (
          <List data-testid="chatList" sx={{ paddingTop: "0px" }}>
            {chats.map((chatData: IPrivateChatData) => (
              <PrivateChatListItem
                chatData={chatData}
                isSelected={selectedChatId === chatData.id}
                key={chatData.id}
              />
            ))}
          </List>
        )}
        {!isChatLoading && !isMoreChats && chats.length !== 0 && (
          <EmptyRecordView emptyMessage={t("endOfTheListMessage")} />
        )}
        {!isChatLoading && chats.length === 0 && <EmptyRecordView />}
        {isChatLoading && (
          <ProgressLoader
            label={tNs("chatLoadingMessage")}
            style={{
              height: "auto",
            }}
          />
        )}
      </>
    </ScrollListContainer>
  );
};
